import {patchState, signalStore, withComputed, withMethods, withState} from "@ngrx/signals";
import {computed, inject, Signal} from "@angular/core";
import {AuthApiService} from "./auth.api.service";
import {map, tap} from "rxjs/operators";
import {catchError, Observable, of, throwError} from "rxjs";
import {Router} from "@angular/router";
import {LoginUser} from "../../../login-users/shared/login-user.model";
import {RoleHierarchyService} from "./role-hierarchy.service";
import {User} from "../../../login-users/shared/user/user.model";

export type AuthStore = InstanceType<typeof authStore>;

type LoginUserState = {
    loggedInUser: User | null;
    authenticated: boolean;
    redirectUrl: string;
}

export const authStore = signalStore(
    {providedIn: 'root'},

    withState<LoginUserState>({
        loggedInUser: null,
        authenticated: false,
        redirectUrl: ''
    }),

    withComputed((store) => ({
        isLoggedIn: computed(() => store.loggedInUser() !== null),
        isAuthenticated: computed(() => store.authenticated())
    })),

    withMethods((
        store,
        roleHierarchy = inject(RoleHierarchyService),
        api = inject(AuthApiService),
        router = inject(Router)
    ) => ({

        hasAuthority(authority: string): Signal<boolean> {
            authority = authority.includes('ROLE_') ? authority : authority = `ROLE_${authority}`;
            return computed(() => {
                if (!store.isLoggedIn()) { return false; }
                return roleHierarchy.anyRoleReachable(store.loggedInUser().roles, authority) ;
            })
        },

        hasRole(role: string): Signal<boolean> {
            return computed(() => {
                if (!store.isLoggedIn()) { return false; }
                return store.loggedInUser().roles.includes( role ) || store.loggedInUser().roles.includes( `ROLE_${role}` );
            })
        },

        hasAnyRole(...roles: string[]): Signal<boolean> {
            return computed(() => {
                if (!store.isLoggedIn()) {
                    return false;
                }
                return store.loggedInUser().roles.some(roles.includes);
            })
        },

        login(username: string, password: string): void {
            api.login(username, password)
                .pipe(
                    map(response => {
                        if (response?.twoFactorUsed) throw new Error('TwoFactorAuthRequired');
                        return api.getLoggedInUser();
                    }),
                    catchError((error: any, caught: any) => {
                        /*
                        if (error.message === 'TwoFactorAuthRequired') {
                            // Redirect to two factor auth page
                            router.navigate(['/kaksivaiheinen-tunnistautuminen/kirjaudu']).then();
                            return of(null);
                        }
                        */

                        return throwError(() => error);

                    })
                )
                .subscribe((val) => {

                     val.pipe()
                        .subscribe((response) => {


                            patchState(store, {loggedInUser: response.authenticatedUser, authenticated: true});
                            //TODO: url parameterize return path after login
                            router.navigate(['/dashboard']).then()

                     })

                });
        },

        logout(): Observable<any> {
            return api.logout().pipe(tap(() => this.userLoggedOut()));
        },

        getLoggedInUser(): Promise<Signal<LoginUser>> {
            return new Promise(resolve => {
                //already authenticated user
                if (store.isLoggedIn() && store.authenticated()) resolve(store.loggedInUser);

                //not authenticated user
                this.fetchSessionUser().subscribe(resolve);
            })
        },

        fetchSessionUser(): Observable<Signal<LoginUser>> {
          return api.getLoggedInUser().pipe(
              tap( auth =>
                  patchState(store, {loggedInUser: auth.authenticatedUser, authenticated: auth.authenticated})
              ),
              map(auth => store.loggedInUser as Signal<LoginUser>)
          )
        },

        userLoggedOut(): void {

            patchState(store, {loggedInUser: null, authenticated: false})

            if(store.redirectUrl() === '') { return; }

            router.navigate([store.redirectUrl()]).then(()=>patchState(store, {redirectUrl: ''}));

        },

        disableTwoFactorAuth(): void {
            if (!store.loggedInUser()) return;
            const user = store.loggedInUser();
            user.twoFactorEnabled = false;
            patchState(store, {loggedInUser: user});
        },

        enableTwoFactorAuth(): void {
            if (!store.loggedInUser()) return;
            const user = store.loggedInUser();
            user.twoFactorEnabled = true;
            patchState(store, {loggedInUser: user});
        },

        setRedirectUrl(url: string): void {
            patchState(store, {redirectUrl: url});
        }
    }))
)